import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { extractNestedAnswers } from "@/encounters/components/PostpartumEncounter/utils/postpartum";
import {
  PostpartumActivityNutritionQuestionsDisplayNames,
  PostpartumActivityNutritionQuestionsLinkIds,
  PostpartumEncounterQuestionnaireURLs,
} from "@/encounters/constants/postpartum-encounter";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";

const ActivityAndNutritionDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          PostpartumEncounterQuestionnaireURLs.ActivityNutrition,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  // If no activity & nutrition data has ever been recorded
  if (!currentVersion) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Activity & Nutrition</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No activity & nutrition data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  // If activity & nutrition data exists but no previous version to compare against,
  // create an empty previous version to reuse the existing diff logic
  const effectivePreviousVersion =
    previousVersion || ({ item: [] } as unknown as QuestionnaireResponse);

  // Extract values for all activity & nutrition fields using extractNestedAnswers
  const previousAnswers = extractNestedAnswers(effectivePreviousVersion);
  const currentAnswers = extractNestedAnswers(currentVersion);

  const previousActivityType =
    previousAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionType
    ]?.join(", ") || "";
  const currentActivityType =
    currentAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionType
    ]?.join(", ") || "";

  const previousActivityTypeOther =
    previousAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionTypeOther
    ]?.join(", ") || "";
  const currentActivityTypeOther =
    currentAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionTypeOther
    ]?.join(", ") || "";

  const previousDietaryRestrictions =
    previousAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionDietaryRestrictions
    ]?.join(", ") || "";
  const currentDietaryRestrictions =
    currentAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionDietaryRestrictions
    ]?.join(", ") || "";

  const previousBeveragesCaffeine =
    previousAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionBeveragesCaffeine
    ]?.join(", ") || "";
  const currentBeveragesCaffeine =
    currentAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionBeveragesCaffeine
    ]?.join(", ") || "";

  const previousMeals =
    previousAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionMeals
    ]?.join(", ") || "";
  const currentMeals =
    currentAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionMeals
    ]?.join(", ") || "";

  const previousWater =
    previousAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionWater
    ]?.join(", ") || "";
  const currentWater =
    currentAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionWater
    ]?.join(", ") || "";

  const previousBeverages =
    previousAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionBeverages
    ]?.join(", ") || "";
  const currentBeverages =
    currentAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionBeverages
    ]?.join(", ") || "";

  const previousNotes =
    previousAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionNotes
    ]?.join(", ") || "";
  const currentNotes =
    currentAnswers[
      PostpartumActivityNutritionQuestionsLinkIds.PostpartumActivityNutritionNotes
    ]?.join(", ") || "";

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Activity & Nutrition</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionType}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentActivityType}
                  previous={previousActivityType}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionTypeOther
                  }
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentActivityTypeOther}
                  previous={previousActivityTypeOther}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionDietaryRestrictions
                  }
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentDietaryRestrictions}
                  previous={previousDietaryRestrictions}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionBeveragesCaffeine
                  }
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentBeveragesCaffeine}
                  previous={previousBeveragesCaffeine}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionMeals
                  }
                </TextWrapper>
                <WordsDiff showRemoved current={currentMeals} previous={previousMeals} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionWater
                  }
                </TextWrapper>
                <WordsDiff showRemoved current={currentWater} previous={previousWater} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionBeverages
                  }
                </TextWrapper>
                <WordsDiff showRemoved current={currentBeverages} previous={previousBeverages} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionNotes
                  }
                </TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionType}
                </TextWrapper>
                <WordsDiff current={currentActivityType} previous={previousActivityType} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionTypeOther
                  }
                </TextWrapper>
                <WordsDiff
                  current={currentActivityTypeOther}
                  previous={previousActivityTypeOther}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionDietaryRestrictions
                  }
                </TextWrapper>
                <WordsDiff
                  current={currentDietaryRestrictions}
                  previous={previousDietaryRestrictions}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionBeveragesCaffeine
                  }
                </TextWrapper>
                <WordsDiff
                  current={currentBeveragesCaffeine}
                  previous={previousBeveragesCaffeine}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionMeals
                  }
                </TextWrapper>
                <WordsDiff current={currentMeals} previous={previousMeals} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionWater
                  }
                </TextWrapper>
                <WordsDiff current={currentWater} previous={previousWater} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionBeverages
                  }
                </TextWrapper>
                <WordsDiff current={currentBeverages} previous={previousBeverages} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumActivityNutritionQuestionsDisplayNames.PostpartumActivityNutritionNotes
                  }
                </TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default ActivityAndNutritionDiff;
